import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GameId } from "@mental-mint/shared-types";
import { getPlayer, shouldShowComingSoonModal, getEnvironment, listenForCloseOriginalTab } from "@mental-mint/shared-utils";
import { Page } from "../Page/Page";
import { PageConfig } from "../Page/PageTypes";
import { NavBar } from "../NavBar/NavBar";
import { RootState } from "../Store/store";
import { initializeSession, Game } from "../Store/gameSessionSlice";
import { pageRegistry } from "../Page/PageRegistryCore";
import { commonPagesRegistry } from "../Page/CommonPages";
import { CommonPageId } from "../Page/PageTypes";
import { logger } from '@mental-mint/shared-utils';
import ModalStylized from '../ModalStylized/ModalStylized';
import { ButtonStylized, ButtonStylizedType } from '../ButtonStylized/ButtonStylized';
import EmailListSignup from '../EmailListSignup/EmailListSignup';
import { navigationRegistry } from '../Navigation/NavigationRegistry';

interface GameControllerProps {
  gameId: GameId;
  game: Game; // Game settings must be passed as prop since they're app-specific
}

interface PageState {
  currentPageId: string | null;
  pageStack: string[];
  pageConfigs: Record<string, PageConfig>;
  // Stores navigation props per page - prevents data loss when modals close
  navigationPropsMap: Record<string, any>; // pageId -> navigationProps
  // Stores navigation timestamp per page - ensures React remounts components with fresh props
  navigationTimestampMap: Record<string, number>; // pageId -> timestamp
}

// Maximum number of pages to keep in navigation history
const MAX_PAGE_HISTORY = 10;

export const GameController: React.FC<GameControllerProps> = ({ gameId, game }) => {
  const dispatch = useDispatch();
  const { currentGame, player, isInitialized } = useSelector((state: RootState) => state.gameSession);
  
  // State to track if coming soon modal should be shown
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  
  // Initialize Redux store and player on mount
  useEffect(() => {
    const initializeGameSession = async () => {
      logger.debug("Attempting to initialize game session for game: ", gameId);
      try {
        const gamePlayer = getPlayer();
        
        // Extract pageRegistry before passing to Redux (it's not serializable)
        const { pageRegistry: _, ...serializableGame } = game;
        
        dispatch(initializeSession({
          gameId,
          game: serializableGame,
          player: gamePlayer
        }));
        
        // Check if we should show coming soon modal
        if (shouldShowComingSoonModal(game.metadata.launchStatus)) {
          setShowComingSoonModal(true);
        }
      } catch (error) {
        logger.error('Failed to initialize game session:', error);
      }
    };

    if (!isInitialized || currentGame?.metadata.gameId !== gameId) {
      initializeGameSession();
    }
  }, [gameId, dispatch, game, isInitialized, currentGame]);
  
  // Page system state (for which page and/or modal to display).
  const [pageState, setPageState] = useState<PageState>({
    currentPageId: null,
    pageStack: [],
    pageConfigs: {},
    navigationPropsMap: {},
    navigationTimestampMap: {}
  });

  // Games must initialize the page system
  const isPageSystemReady = Object.keys(pageState.pageConfigs).length > 0 && isInitialized;  
  
  // Page system methods that games can call
  const initializePageSystem = useCallback((pages: Record<string, PageConfig>, initialPageId?: string) => {
    setPageState({
      currentPageId: initialPageId || null,
      pageStack: initialPageId ? [initialPageId] : [],
      pageConfigs: pages,
      navigationPropsMap: {},
      navigationTimestampMap: {}
    });
  }, []);
  
  /** Navigates to the page with the provided Id, using navigationProps as parameters
   * for rendering this page.
   * @param allowStacking - If true, allows modals to stack on top of each other. If false (default),
   *                        navigating to a modal will clear existing modals from the stack first.
   */
  const navigateToPage = useCallback((pageId: string, navigationProps?: any, allowStacking: boolean = false) => {
    setPageState(prev => {
      const targetPageConfig = prev.pageConfigs[pageId];
      const isTargetModal = targetPageConfig?.modalPageConfig !== undefined;
      
      let baseStack = prev.pageStack;
      
      // If navigating to a modal and stacking is NOT allowed, clear existing modals first
      if (isTargetModal && !allowStacking && baseStack.length > 0) {
        // Find the last non-modal page in the stack (the background page)
        let lastNonModalIndex = baseStack.length - 1;
        for (let i = baseStack.length - 1; i >= 0; i--) {
          const pageConfig = prev.pageConfigs[baseStack[i]];
          if (pageConfig?.modalPageConfig === undefined) {
            lastNonModalIndex = i;
            break;
          }
        }
        // Keep only up to and including the last non-modal page
        baseStack = baseStack.slice(0, lastNonModalIndex + 1);
      }
      
      // Build new stack with the new page
      const newStack = [...baseStack, pageId];
      
      // Trim history to maximum size, keeping the most recent pages
      const trimmedStack = newStack.length > MAX_PAGE_HISTORY 
        ? newStack.slice(-MAX_PAGE_HISTORY)
        : newStack;
      
      // Store props per page (not globally) - prevents modal closures from clearing other pages' props
      const newNavigationPropsMap = { ...prev.navigationPropsMap };
      if (navigationProps !== undefined) {
        newNavigationPropsMap[pageId] = navigationProps;
      }
      
      // Update timestamp to force React key change - ensures component remounts with fresh props
      const newNavigationTimestampMap = { ...prev.navigationTimestampMap };
      newNavigationTimestampMap[pageId] = Date.now();
      
      return {
        ...prev,
        currentPageId: pageId,
        pageStack: trimmedStack,
        navigationPropsMap: newNavigationPropsMap,
        navigationTimestampMap: newNavigationTimestampMap
      };
    });
  }, []);

  /** Navigates back to the previous page in the stack. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigateBack = useCallback(() => {
    setPageState(prev => {
      if (prev.pageStack.length <= 1) {
        return prev; // Can't go back from first page
      }
      
      const newStack = prev.pageStack.slice(0, -1);
      const newCurrentPageId = newStack[newStack.length - 1];
      
      logger.debug(`Navigating back from page: ${pageState.currentPageId} to page: ${newCurrentPageId}`);
      
      // Remove navigationProps for the page we're navigating away from
      const newNavigationPropsMap = { ...prev.navigationPropsMap };
      delete newNavigationPropsMap[prev.currentPageId!];
      
      return {
        ...prev,
        currentPageId: newCurrentPageId,
        pageStack: newStack,
        navigationPropsMap: newNavigationPropsMap,
        navigationTimestampMap: prev.navigationTimestampMap // Preserve timestamps
      };
    });
  }, [pageState]);

  /** Function to close the current modal page. */
  const onPageClose = useCallback(() => {
    setPageState(prev => {
      // Can't go back from first page
      if (prev.pageStack.length <= 1) {
        logger.debug(`No page to go back to, staying on current page: ${prev.currentPageId}`);
        return prev;
      }
      
      // Go back one step in the stack (could be another modal or the background page)
      const newStack = prev.pageStack.slice(0, -1);
      const newCurrentPageId = newStack[newStack.length - 1];
      logger.debug(`Closing modal, going back to page: ${newCurrentPageId}`);
      
      // Remove navigationProps only for the page being closed, preserve others
      const newNavigationPropsMap = { ...prev.navigationPropsMap };
      delete newNavigationPropsMap[prev.currentPageId!];
      
      return {
        ...prev,
        currentPageId: newCurrentPageId,
        pageStack: newStack,
        navigationPropsMap: newNavigationPropsMap,
        navigationTimestampMap: prev.navigationTimestampMap // Preserve timestamps
      };
    });  
  }, []);
  
  // Auto-initialize page system from game settings
  useEffect(() => {
    if (isInitialized && game.pageConfigs && game.initialPageId) {
      logger.debug("Auto-initializing page system for game:", gameId);
      
      // Register common pages first
      const commonPageRenderers = commonPagesRegistry.getPageRenderers();
      pageRegistry.registerPages(commonPageRenderers);
      
      // Register game-specific pages
      const gamePageRenderers = game.pageRegistry.getPageRenderers();
      pageRegistry.registerPages(gamePageRenderers);
      
      // Add Terms page configuration to page configs (as a modal, not in nav)
      const termsPageConfig: PageConfig = {
        id: CommonPageId.Terms,
        modalPageConfig: {
          humanReadableTitle: "Terms & Privacy"
        },
        navItem: null // Don't show in navigation bar
      };
      
      // Add Login page configuration (as a modal, not in nav)
      const loginPageConfig: PageConfig = {
        id: CommonPageId.Login,
        modalPageConfig: {
          humanReadableTitle: "Sign up"
        },
        navItem: null
      };
      
      // Add AuthError page configuration (as a modal, not in nav)
      const authErrorPageConfig: PageConfig = {
        id: CommonPageId.AuthError,
        modalPageConfig: {
          humanReadableTitle: "Authentication Error"
        },
        navItem: null
      };
      
      // Add MagicLinkCallback page configuration (full screen, not modal)
      const magicLinkCallbackPageConfig: PageConfig = {
        id: CommonPageId.MagicLinkCallback,
        modalPageConfig: undefined, // Full page - user can't dismiss during verification
        navItem: null
      };
      
      // Add CloseTab page configuration (full screen, not modal)
      const closeTabPageConfig: PageConfig = {
        id: CommonPageId.CloseTab,
        modalPageConfig: undefined, // Full page - user needs to close the tab
        navItem: null
      };
      
      // Add NotificationPermission page configuration (as a modal, not in nav)
      const notificationPermissionPageConfig: PageConfig = {
        id: CommonPageId.NotificationPermission,
        modalPageConfig: {
          humanReadableTitle: "Enable Notifications"
        },
        navItem: null
      };
      
      // Initialize the page system with the game's configuration plus common modal pages
      const pageConfigsWithCommonModals = {
        ...game.pageConfigs,
        [CommonPageId.Terms]: termsPageConfig,
        [CommonPageId.Login]: loginPageConfig,
        [CommonPageId.AuthError]: authErrorPageConfig,
        [CommonPageId.MagicLinkCallback]: magicLinkCallbackPageConfig,
        [CommonPageId.CloseTab]: closeTabPageConfig,
        [CommonPageId.NotificationPermission]: notificationPermissionPageConfig
      };
      
      // Initialize with the initial page
      initializePageSystem(pageConfigsWithCommonModals, game.initialPageId);
      
      // Check if URL has a magic link token - if so, show the callback page instead
      const urlParams = new URLSearchParams(window.location.search);
      const hasMagicLinkToken = urlParams.has('token');
      
      if (hasMagicLinkToken) {
        logger.debug("Detected magic link token in URL - navigating to callback page");
        // Navigate to the magic link callback page to handle verification
        // The callback page will call onRequestClose which navigates back to initial page
        setTimeout(() => {
          navigateToPage(CommonPageId.MagicLinkCallback);
        }, 0);
      } else if (game.initialModalPageIds && game.initialModalPageIds.length > 0) {
        // If there are initialModalPageIds (and no token), navigate to each in reverse order
        // This stacks modals so the first in the array shows first (is on top of stack)
        // When user closes it, the next modal in the array appears
        logger.debug("Navigating to initial modal pages:", game.initialModalPageIds);
        setTimeout(() => {
          // Reverse so first modal in array is on top (shows first)
          // Use allowStacking: true to keep modals stacked on page load
          [...game.initialModalPageIds].reverse().forEach((modalPageId: string) => {
            navigateToPage(modalPageId, undefined, true);
          });
        }, 0);
      }
    }
  }, [isInitialized, game, initializePageSystem, gameId, navigateToPage]);

  // Listen for signal to close this tab (magic link opened in new tab)
  // Web-only enhancement - mobile apps ignore this
  useEffect(() => {
    const cleanup = listenForCloseOriginalTab(() => {
      logger.debug('[Cross-Tab] Magic link verified in new tab - attempting to close this tab');
      
      // Try to close this window
      window.close();
      
      // If window.close() fails (browser may block it), show "close this tab" page
      // Check after a short delay if window is still open
      setTimeout(() => {
        if (!window.closed) {
          logger.debug('[Cross-Tab] Could not auto-close tab - showing close message');
          navigateToPage(CommonPageId.CloseTab);
        }
      }, 100);
    });
    
    return cleanup;
  }, [navigateToPage]);
  
  // Register navigation function with global registry for services to use
  useEffect(() => {
    navigationRegistry.register(navigateToPage);
    
    return () => {
      navigationRegistry.unregister();
    };
  }, [navigateToPage]);
    
  // ARCHITECTURAL GUARANTEE: 
  // Beyond this point, all components can safely assume currentGame and player exist in Redux.
  // GameController ensures these values are available before rendering any game content.
  // Components should NOT defensively check for null/undefined - they can trust the data exists.
  
  // Show loading state while Redux initializes or if essential data is missing
  if (!isInitialized || !currentGame || !player) {
    return (
      <div className="game-controller">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100dvh',
          color: 'white',
          fontFamily: game.fontPrimary,
          fontSize: '1.2rem'
        }}>
          Initializing game...
        </div>
      </div>
    );
  }
  
  // Page system must be initialized
  if (!isPageSystemReady) {
    return (
      <div className="game-controller">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100dvh',
          color: 'white',
          fontFamily: game.fontPrimary,
          fontSize: '1.2rem',
          textAlign: 'center',
          gap: '1rem'
        }}>
          <div>Game is loading...</div>
          <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>
            Initializing page system
          </div>
        </div>
      </div>
    );
  }

  // Render the page-based system
  const { currentPageId, pageConfigs } = pageState;  // Get navigation items for nav bar
  const navItems = Object.entries(pageConfigs)
    .map(([pageId, config]) => config.navItem ? { ...config.navItem, pageId } : null)
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Find background page (non-modal page)
  let backgroundPageConfig: PageConfig | null = null;
  let backgroundPageId: string | null = null;
  for (let i = pageState.pageStack.length - 1; i >= 0; i--) {
    // Look for the latest non-modal page in the stack
    const stackPageId = pageState.pageStack[i];
    const config = pageConfigs[stackPageId];
    if (config && !config.modalPageConfig) {
      backgroundPageConfig = config;
      backgroundPageId = stackPageId;
      break;
    }
  }

  // Find modal page (current page if it's modal)
  let modalPageConfig: PageConfig | null = null;
  let modalPageId: string | null = null;
  if (currentPageId) {
    const config = pageConfigs[currentPageId];
    if (config && !!config.modalPageConfig) {
      modalPageConfig = config;
      modalPageId = currentPageId;
    }
  }

  // Render
  const gameContent = (
    <div className="game-controller">
        {/* Coming Soon Modal - shown when game is not released yet */}
        {showComingSoonModal && (
          <ModalStylized
            isOpen={true}
            title="" // Empty title, we'll render our own
            iconImage={game.gameIconLarge}
          >
            <div style={{ 
              textAlign: 'center', 
              padding: '1rem',
              fontFamily: game.fontPrimary || 'Open Sans',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
              width: '100%',
              maxWidth: '100%',
              margin: '0 auto',
              boxSizing: 'border-box'
            }}>
              {/* Game Title with primary color */}
              <p style={{ 
                fontSize: '2.5rem', 
                margin: 0,
                fontFamily: game.fontPrimary || 'Open Sans',
                fontWeight: 'bold',
                color: game.colorPrimary || '#fff'
              }}>
                {currentGame?.metadata.gameName || "Coming Soon"}
              </p>
              
              <p style={{ 
                fontSize: '1.5rem', 
                margin: 0,
                fontFamily: game.fontPrimary || 'Open Sans',
                fontWeight: 'bold',
                color: '#fff'
              }}>
                This game is coming soon!
              </p>
              <p style={{ 
                fontSize: '1.1rem', 
                opacity: 0.85, 
                margin: 0,
                fontFamily: game.fontSecondary || 'Open Sans',
                color: '#fff'
              }}>
                Stay tuned for updates by subscribing below:
              </p>
              
              {/* Email signup */}
              <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <EmailListSignup />
              </div>
              
              {/* In development, show a bypass button */}
              {getEnvironment() === 'development' && (
                <ButtonStylized
                  buttonType={ButtonStylizedType.Secondary}
                  text="Continue to Game (Dev Only)"
                  onClick={() => setShowComingSoonModal(false)}
                  additionalStyles={{ 
                    marginTop: '1rem',
                    fontFamily: game.fontPrimary || 'Open Sans',
                    fontSize: '1rem',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              )}
            </div>
          </ModalStylized>
        )}
        
        {/* Navigation Bar */}
        {navItems.length > 0 && !showComingSoonModal && (
          <NavBar
            items={navItems}
            activeItemId={currentPageId}
            onItemClick={navigateToPage}
          />
        )}      
        
        {/* Background Page */}
        {backgroundPageConfig && backgroundPageId && !showComingSoonModal && (
          <Page
            key={`${backgroundPageId}-${pageState.navigationTimestampMap[backgroundPageId] || 0}`}
            pageId={backgroundPageId}
            config={backgroundPageConfig}
            onRequestClose={onPageClose}
            navigateToPage={navigateToPage}
            navigationProps={pageState.navigationPropsMap[backgroundPageId] || null}
            hasNavBar={navItems.length > 0}
          />
        )}

        {/* Modal Overlay */}
        {modalPageConfig && modalPageId && !showComingSoonModal && (
          <Page
            key={`${modalPageId}-${pageState.navigationTimestampMap[modalPageId] || 0}`}
            pageId={modalPageId}
            config={modalPageConfig}
            onRequestClose={onPageClose}
            navigateToPage={navigateToPage}
            navigationProps={pageState.navigationPropsMap[modalPageId] || null}
            hasNavBar={navItems.length > 0}
          />
        )}
      </div>
  );
  
  return gameContent;
};